import { Link, Navigate, Outlet, useLocation } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { PanelLeft, Settings } from 'lucide-react'
import { adminMeQuery } from '@/features/admin/shared/api'
import { useFrenteNav } from '@/features/erp/frente-state'
import { areaOfPath } from '@/features/erp/frentes'
import { AreaTabs } from '@/features/erp/ui/AreaTabs'
import { EntityPeek } from '@/features/erp/ui/EntityPeek'
import { Trail } from '@/features/erp/ui/Trail'
import { ApiError, isSessionError } from '@/lib/api'
import { meQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { areaHasTree } from './staff-nav'
import { iconOnly, quietButton, secondaryButton, small } from './StatusScreens'
import { useRecordTrail } from './trail-store'
import { LoginScreen, SetPasswordScreen } from './LoginScreen'
import { Sidebar, useSidebarCollapsed } from './Sidebar'
import { CommandPalette } from './CommandPalette'
import { Toaster } from '@/components/ui/Toaster'
import { ErrorScreen, SplashScreen } from './StatusScreens'

/* Session gate (GET /me) + layout: sidebar on the left (Staff menu of the frente under /staff, or the Configurações
   menu), content up to 1280px, centered. The PanelLeft button on top of the content collapses the sidebar to the icon rail.
   Staff: on top of the content the trail (with "← Voltar"), the gear to Configurações (admin only) and the tabs of the
   current experience (an experience with a tree in the sidebar, e.g. Sponsor, shows them only where the tree is hidden:
   the icon rail and phones). Configurações (the old Admin space + /configuracoes) is admin only: anyone else goes to /staff
   (the entry experience of their frente). */

/* The server comes back to /?denied=1 (account not on the panel list) or /?login_error=1 (sign-in failed). */
function urlFlag(name: 'denied' | 'login_error'): boolean {
    try {
        return new URLSearchParams(window.location.search).get(name) === '1'
    } catch {
        return false
    }
}

export function AppShell() {
    const me = useQuery(meQuery)
    const { pathname } = useLocation()
    const [collapsed, setCollapsed] = useSidebarCollapsed()
    const inAdmin = pathname === '/staff' || pathname.startsWith('/staff/')
    // Portal do cliente de Embaixadores: tela de quem aprova, sem o menu do Staff
    const bare = pathname === '/staff/embaixadores/portal'
    const adminMe = useQuery({ ...adminMeQuery, enabled: me.isSuccess && !inAdmin })
    useRecordTrail(pathname, me.isSuccess && inAdmin && !bare)

    // Link de acesso: cria a senha sem sessão (não depende do /me)
    if (pathname === '/definir-senha') return <SetPasswordScreen />
    if (urlFlag('denied')) return <LoginScreen denied />
    if (me.error && isSessionError(me.error)) return <LoginScreen failed={urlFlag('login_error')} />
    if (me.error instanceof ApiError && me.error.code === 'forbidden') return <LoginScreen denied />
    if (me.isPending) return <SplashScreen />
    if (me.isError) return <ErrorScreen error={me.error} onRetry={() => void me.refetch()} />

    // Configurações: só o perfil admin (sem mostrar o conteúdo antes de saber)
    if (!inAdmin) {
        if (adminMe.isPending) return <SplashScreen />
        if (adminMe.data?.role !== 'admin') return <><SplashScreen /><Navigate to="/staff" replace /></>
    }

    if (bare) {
        return (
            <div className="cf-shell min-h-dvh">
                <main className="cf-main mx-auto flex w-full max-w-[1280px] flex-col gap-4 px-4 pt-4 pb-8 sm:px-6">
                    <Outlet />
                </main>
                <Toaster />
            </div>
        )
    }

    return (
        <div className="cf-shell flex min-h-dvh">
            <Sidebar me={me.data} workspace={inAdmin ? 'admin' : 'main'} />
            <div className="min-w-0 flex-1">
                <div className={cn('mx-auto flex w-full max-w-[1280px] items-center gap-2 px-4 pt-3', !inAdmin && 'cf-topbar')}>
                    <button
                        type="button"
                        className={cn(quietButton, small, iconOnly, 'cf-only-wide flex-none')}
                        aria-label={collapsed ? 'Abrir o menu' : 'Recolher o menu'}
                        title={collapsed ? 'Abrir o menu' : 'Recolher o menu'}
                        aria-pressed={collapsed}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <PanelLeft aria-hidden="true" />
                    </button>
                    {inAdmin && <StaffBar />}
                </div>
                <main className="cf-main mx-auto flex w-full max-w-[1280px] flex-col gap-4 px-6 pt-1 pb-8">
                    {inAdmin && <StaffAreaTabs pathname={pathname} collapsed={collapsed} />}
                    <Outlet />
                </main>
            </div>
            <CommandPalette />
            <EntityPeek />
            <Toaster />
        </div>
    )
}

/** Staff: trail + "← Voltar" and, for the admin, the gear to Configurações. */
function StaffBar() {
    const { role, nav } = useFrenteNav()
    return (
        <>
            <Trail nav={nav} />
            {role === 'admin' && (
                <Link to="/" className={cn(secondaryButton, small, 'flex-none')} title="Configurações (área de administração)" aria-label="Configurações">
                    <Settings size={15} aria-hidden="true" /><span className="max-[520px]:hidden">Configurações</span>
                </Link>
            )}
        </>
    )
}

/** Staff: tabs with the screens of the current experience of the frente menu (none outside the menu). With a tree in the
    expanded sidebar the tabs only show on phones (≤760px, the sidebar is the top bar there). */
function StaffAreaTabs({ pathname, collapsed }: { pathname: string; collapsed: boolean }) {
    const { nav } = useFrenteNav()
    const hit = areaOfPath(nav, pathname)
    if (!hit || hit.tabs.length < 2) return null
    const tabs = <AreaTabs area={hit.area} tabs={hit.tabs} current={hit.current} />
    return areaHasTree(hit.area) && !collapsed ? <div className="cf-only-narrow">{tabs}</div> : tabs
}
